import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EnvironmentInjector,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  createEnvironmentInjector,
} from '@angular/core';
import {CommonModule, NgComponentOutlet} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {DialogModule} from 'primeng/dialog';
import {Router} from '@angular/router';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {Subject} from 'rxjs';
import {finalize, takeUntil} from 'rxjs/operators';
import {
  CustomFieldViewModel,
  Field,
  FieldService,
  Season,
} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {ModalFieldEditExtended} from '@app/pages/electronic-field-map/pages/gis-fields/components/modals/modal-field-edit-extended/modal-field-edit-extended';
import {PointPolygonDetailsModalComponent} from '@app/pages/weather/pages/gis-weather/components/point-polygon-details-modal/point-polygon-details-modal.component';
import {Loading} from '@components/ui/loading/loading';
import {SecondaryButton} from '@components/button/secondary-button/secondary-button';
import {PassportManagerService} from '@app/pages/electronic-field-map/pages/fields/passport/passport-manager.service';
import {PolygonConverterUtil} from '@app/utils/polygon-converter.util';
import {environment} from '@root/src/environments/environment';
import {TabsDynamic, TabItem} from '@components/ui/tabs/tabs-dynamic';
import {FilterOperator} from '@app/utils/filter-operator';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import {FieldDetailsIndicesTabComponent} from '@app/pages/electronic-field-map/pages/gis-fields/components/modals/field-details-dialog/components/field-details-indices-tab/field-details-indices-tab.component';
import {FieldDetailsAtmTabComponent} from '@app/pages/electronic-field-map/pages/gis-fields/components/modals/field-details-dialog/components/field-details-atm-tab/field-details-atm-tab.component';
import {FieldDetailsAgroTabComponent} from '@app/pages/electronic-field-map/pages/gis-fields/components/modals/field-details-dialog/components/field-details-agro-tab/field-details-agro-tab.component';
import {FieldDetailsAnomaliesTabComponent} from '@app/pages/electronic-field-map/pages/gis-fields/components/modals/field-details-dialog/components/field-details-anomalies-tab/field-details-anomalies-tab.component';

@Component({
  selector: 'app-field-details-dialog',
  standalone: true,
  imports: [
    CommonModule,
    NgComponentOutlet,
    FormsModule,
    TranslateModule,
    DialogModule,
    Loading,
    SecondaryButton,
    TabsDynamic,
    PointPolygonDetailsModalComponent,
    FieldDetailsIndicesTabComponent,
    FieldDetailsAtmTabComponent,
    FieldDetailsAgroTabComponent,
    FieldDetailsAnomaliesTabComponent,
  ],
  templateUrl: './field-details-dialog.component.html',
  styleUrls: ['./field-details-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldDetailsDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly fieldService = inject(FieldService);
  private readonly translate = inject(TranslateService);
  private readonly passportManager = inject(PassportManagerService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly regionManager = inject(RegionManagerService);
  private readonly destroy$ = new Subject<void>();
  private readonly envInjector = inject(EnvironmentInjector);

  /** Main dialog shell: indices (default), weather, ATM, field edit. */
  shellTabs: TabItem[] = [];
  shellActiveTab = 'indices';
  shellMeteoContentReady = false;
  shellAtmContentReady = false;
  shellAgroContentReady = false;
  shellAnomaliesContentReady = false;
  shellEditContentReady = false;
  editTabInjector: EnvironmentInjector | null = null;
  readonly editTabComponent = ModalFieldEditExtended;

  fieldPolygonJson: string | null = null;

  @Input() field: Field | null | any = null;
  @Input() appendTo?: string;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<void>();

  visible = false;
  isLoading = false;
  customField: CustomFieldViewModel & { seasons: Season[] } | null = null;
  dataUpdatedAt: Date | null = null;
  /** Season id from map click when opening the dialog (may be inactive). */
  preferredSeasonId: string | null = null;

  fieldCenter: { lat: number; lng: number } | null = null;
  mapCenter: [number, number] = [environment.map.lng, environment.map.lat];

  /** Region breadcrumb from dictionary; null falls back to embedded region getter. */
  regionPathDisplay: string | null = null;

  @ViewChild('indicesTabTpl', { static: false }) indicesTabTpl!: TemplateRef<unknown>;
  @ViewChild('meteoTabTpl', { static: false }) meteoTabTpl!: TemplateRef<unknown>;
  @ViewChild('atmTabTpl', { static: false }) atmTabTpl!: TemplateRef<unknown>;
  @ViewChild('agroTabTpl', { static: false }) agroTabTpl!: TemplateRef<unknown>;
  @ViewChild('anomaliesTabTpl', { static: false }) anomaliesTabTpl!: TemplateRef<unknown>;
  @ViewChild('editTabTpl', { static: false }) editTabTpl!: TemplateRef<unknown>;

  ngOnInit(): void {
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      void this.refreshRegionLabel();
      this.cdr.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    this.shellTabs = [
      { id: 'indices', label: 'FIELD_DETAILS_V2.TAB_INDICES', template: this.indicesTabTpl },
      { id: 'meteo', label: 'FIELD_DETAILS_V2.TAB_METEO', template: this.meteoTabTpl },
      { id: 'atm', label: 'FIELD_DETAILS_V2.TAB_AGRO_CALENDAR', template: this.atmTabTpl },
      { id: 'agro', label: 'FIELD_DETAILS_V2.TAB_AGRO_MEASURES', template: this.agroTabTpl },
      { id: 'anomalies', label: 'FIELD_DETAILS_V2.TAB_ANOMALIES', template: this.anomaliesTabTpl },
      { id: 'edit', label: 'FIELD_DETAILS_V2.TAB_EDIT', template: this.editTabTpl },
    ];
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disposeEditTabInjector();
  }

  show(field: any, options?: {seasonId?: string | null}): void {
    this.field = field;
    const seasonId = options?.seasonId != null ? String(options.seasonId).trim() : '';
    this.preferredSeasonId = seasonId || null;
    this.visible = true;
    this.shellActiveTab = 'indices';
    this.shellMeteoContentReady = false;
    this.shellAtmContentReady = false;
    this.shellAgroContentReady = false;
    this.shellAnomaliesContentReady = false;
    this.shellEditContentReady = false;
    this.disposeEditTabInjector();
    this.customField = null;
    this.regionPathDisplay = null;
    this.fieldPolygonJson = null;
    this.fieldCenter = null;
    this.mapCenter = [environment.map.lng, environment.map.lat];
    this.loadFieldDetails();
  }

  onHide(): void {
    this.visible = false;
    this.preferredSeasonId = null;
    this.shellActiveTab = 'indices';
    this.shellMeteoContentReady = false;
    this.shellAtmContentReady = false;
    this.shellAgroContentReady = false;
    this.shellAnomaliesContentReady = false;
    this.shellEditContentReady = false;
    this.disposeEditTabInjector();
    this.close.emit();
  }

  get atmCrops(): {id?: string}[] {
    return (this.customField as {crops?: {id?: string}[]} | null)?.crops ?? [];
  }

  get fieldEntity(): any {
    return this.field?.field ?? this.field;
  }

  get fieldDisplayName(): string {
    return this.fieldEntity?.name || this.field?.fieldName || this.field?.name || '';
  }

  get fieldRegionDisplay(): string {
    if (this.regionPathDisplay != null && String(this.regionPathDisplay).trim() !== '') {
      return this.regionPathDisplay;
    }
    return this.flatEmbeddedRegionLabel();
  }

  private flatEmbeddedRegionLabel(): string {
    const r = (this.customField as any)?.region;
    if (!r) {
      return '—';
    }
    if (typeof r === 'string') {
      return r;
    }
    const lang = this.translate.currentLang || 'ru-RU';
    return (
      r.names?.[lang] ||
      r.names?.['ru-RU'] ||
      r.names?.['en-US'] ||
      r.name ||
      r.regionName ||
      '—'
    );
  }

  private async refreshRegionLabel(): Promise<void> {
    const r = (this.customField as any)?.region;
    const rid = r?.id ?? this.fieldEntity?.regionId;
    if (!r && !rid) {
      this.regionPathDisplay = '—';
      this.cdr.markForCheck();
      return;
    }
    if (!rid) {
      this.regionPathDisplay = this.flatEmbeddedRegionLabel();
      this.cdr.markForCheck();
      return;
    }
    try {
      const full = await this.regionManager.getRegionPathStringById(String(rid));
      const parts = full.split(' / ').map(s => s.trim()).filter(Boolean);
      if (parts.length >= 2) {
        this.regionPathDisplay = `${parts[parts.length - 2]} / ${parts[parts.length - 1]}`;
      } else if (parts.length === 1) {
        this.regionPathDisplay = parts[0];
      } else {
        this.regionPathDisplay = this.flatEmbeddedRegionLabel();
      }
    } catch {
      this.regionPathDisplay = this.flatEmbeddedRegionLabel();
    }
    this.cdr.markForCheck();
  }

  onShellTabChange(tabId: string): void {
    this.shellActiveTab = tabId;
    if (tabId === 'meteo') {
      this.shellMeteoContentReady = true;
    }
    if (tabId === 'atm') {
      this.shellAtmContentReady = true;
    }
    if (tabId === 'agro') {
      this.shellAgroContentReady = true;
    }
    if (tabId === 'anomalies') {
      this.shellAnomaliesContentReady = true;
    }
    if (tabId === 'edit' && !this.isLoading && this.customField) {
      this.shellEditContentReady = true;
      if (!this.editTabInjector) {
        this.rebuildEditTabInjector();
      }
    }
    this.cdr.markForCheck();
  }

  onEditEmbeddedClose(result?: { success?: boolean; data?: any } | null): void {
    if (result?.success) {
      if (result.data) {
        this.field = result.data;
      }
      this.save.emit();
      this.loadFieldDetails();
      this.shellActiveTab = 'indices';
    }
    this.cdr.markForCheck();
  }

  private disposeEditTabInjector(): void {
    this.editTabInjector?.destroy();
    this.editTabInjector = null;
  }

  private rebuildEditTabInjector(): void {
    this.disposeEditTabInjector();
    if (!this.field?.id || !this.field) {
      return;
    }
    const data: Record<string, unknown> = {
      model: {...this.field},
      serviceMethod: 'cropMonitoringFieldUpdatePut',
      service: this.fieldService,
      fields: this.passportManager.getEditFieldFormConfig(),
      multiPolygonJson: [],
      embedded: true,
    };
    const config = { data } as DynamicDialogConfig;
    const refStub = {
      close: (r?: unknown) => this.onEditEmbeddedClose(r as { success?: boolean; data?: any } | null),
    } as DynamicDialogRef;
    this.editTabInjector = createEnvironmentInjector(
      [
        { provide: DynamicDialogConfig, useValue: config },
        { provide: DynamicDialogRef, useValue: refStub },
      ],
      this.envInjector,
    );
  }

  onShowMore(): void {
    const fieldId = this.fieldEntity?.id || this.field?.id;
    if (!fieldId) {
      return;
    }
    try {
      this.close.emit();
      this.router.navigate(['/electronic-field-map/fields', fieldId, 'general']);
    } catch (e) {
      console.warn(e);
    }
  }

  /** Called from the indices tab after actions that require a fresh field VM. */
  reloadFieldDetails(): void {
    this.loadFieldDetails();
  }

  private loadFieldDetails(): void {
    if (!this.field?.id) {
      return;
    }
    this.disposeEditTabInjector();
    this.isLoading = true;
    this.fieldService
      .cropMonitoringFieldCustomGetByQueryPost({
        selectFields: ['field', 'agricultureProducerName', 'lastBidDate', 'region', 'crops', 'varieties', 'seasons'],
        filters: [
          {
            filterField: 'id',
            filterOperator: FilterOperator.Equals,
            filterValue: this.field.id,
          },
        ],
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.dataUpdatedAt = new Date();
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (data) => {
          if (data?.length) {
            this.customField = data[0] as CustomFieldViewModel & { seasons: Season[] };
            this.customField.seasons = [...(this.customField.seasons || [])];
            this.field = this.customField.field;
            this.initGeometry();
            void this.refreshRegionLabel();
            if (this.shellEditContentReady && this.shellActiveTab === 'edit') {
              this.rebuildEditTabInjector();
            }
          }
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  private initGeometry(): void {
    const pj = this.fieldEntity?.polygonJson;
    this.fieldPolygonJson = pj || null;
    const coords = PolygonConverterUtil.polygonJsonToCoordinates(pj);
    const c = PolygonConverterUtil.getPolygonCenter(coords);
    this.fieldCenter = c;
    if (c) {
      this.mapCenter = [c.lng, c.lat];
    }
  }
}
